import { useEffect, useState } from "react";
import { CheckIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

import useBaskets from "../../data/useBaskets";
import useStockPermissions, { STOCK_PRICE_LOCKED_MESSAGE } from "../../data/useStockPermissions";
import { IBasketItem } from "../../models/Basket";
import { ITillQuoteLine } from "../../models/till/ITillQuote";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { isValidPartPrice } from "../../utils/priceLimits";
import PartImage from "../stock/PartImage";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";

interface TillBasketLineProps {
  item: IBasketItem;
  quoteLine?: ITillQuoteLine;
  disabled?: boolean;
}

/** One basket line with an inline price edit and a remove button. Parts are unique, so no quantity. */
export default function TillBasketLine({ item, quoteLine, disabled }: TillBasketLineProps) {
  const { updateItem, removeItem } = useBaskets("TillBasketLine");
  const { restricted } = useStockPermissions();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const currentPrice = item.basketPrice ?? item.part?.enteredPrice ?? 0;

  // Stock:AdminOnlyChanges - a line that already has a price sells at it unless an admin changes
  // it. basketPrice is what the API compares with; it's set when the part goes in the basket.
  const priceLocked = restricted && (item.basketPrice ?? 0) > 0;

  useEffect(() => {
    if (!editing) setDraft(currentPrice.toFixed(2));
  }, [currentPrice, editing]);

  const draftNumber = parseFloat(draft);
  const draftValid = Number.isFinite(draftNumber) && draftNumber >= 0 && isValidPartPrice(draftNumber);

  const save = () => {
    if (!draftValid) return;
    updateItem.mutate({ ...item, basketPrice: draftNumber });
    setEditing(false);
  };

  return (
    <li className="flex items-center gap-3 p-3 bg-white">
      <div className="shrink-0">
        <PartImage part={item.part} width="w-16" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-mono text-gray-500 truncate">{item.part?.tagNumber ?? "No tag"}</p>
        <p className="text-sm font-medium text-gray-900 leading-snug line-clamp-2">{item.part?.titleSanitised ?? item.part?.title ?? "Part"}</p>
        {quoteLine ? (
          <div className="mt-1">
            <StatusPill
              text={quoteLine.isMarginScheme ? "Margin scheme" : quoteLine.taxRate > 0 ? `VAT ${quoteLine.taxRate}%` : "Zero rated"}
              colour={quoteLine.isMarginScheme ? StatusPillColour.Purple : quoteLine.taxRate > 0 ? StatusPillColour.Blue : StatusPillColour.Neutral}
            />
          </div>
        ) : null}
      </div>

      {editing ? (
        <div className="flex items-center gap-1">
          <div className="relative">
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">£</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
              className={classNames("h-12 w-28 rounded-md border pl-6 text-right text-lg", draftValid ? "border-gray-300" : "border-red-400")}
            />
          </div>
          <button type="button" onClick={save} disabled={!draftValid} className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-primary-600 text-white disabled:bg-gray-300" aria-label="Save price">
            <CheckIcon className="h-6 w-6" />
          </button>
          <button type="button" onClick={() => setEditing(false)} className="inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100" aria-label="Cancel">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <div className="flex flex-col items-end">
            <button
              type="button"
              disabled={disabled || priceLocked}
              onClick={() => setEditing(true)}
              title={priceLocked ? STOCK_PRICE_LOCKED_MESSAGE : undefined}
              className="inline-flex h-12 items-center gap-2 rounded-md px-3 text-lg font-semibold text-gray-900 hover:bg-gray-100 disabled:hover:bg-transparent"
              aria-label="Edit price"
            >
              {PriceFormat(quoteLine?.lineTotal ?? currentPrice)}
              {!disabled && !priceLocked ? <PencilSquareIcon className="h-5 w-5 text-gray-400" /> : null}
            </button>
            {quoteLine ? (
              // Always both figures, £0.00 included, so a margin-scheme or zero-rated line reads as
              // "no VAT in this price" rather than "VAT not worked out yet".
              <p className="px-3 -mt-1 text-xs text-gray-500 tabular-nums text-right">
                Goods ex VAT {PriceFormat(quoteLine.unitPrice)} · VAT {PriceFormat(Math.max(0, quoteLine.lineTotal - quoteLine.unitPrice))}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={disabled || removeItem.isLoading}
            onClick={() => removeItem.mutate(item.partId)}
            className="inline-flex h-12 w-12 items-center justify-center rounded-md text-red-600 hover:bg-red-50 disabled:text-gray-300 disabled:hover:bg-transparent"
            aria-label="Remove from basket"
          >
            <TrashIcon className="h-6 w-6" />
          </button>
        </div>
      )}
    </li>
  );
}
